import React, { useCallback, useEffect, useState } from 'react';
import { SpellCheck2, Plus, Trash2, Loader2, Wand2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const emptyRule = () => ({ term: '', say_as: '', whole_word: true, match_case: false, enabled: true });

/** Per-word pronunciation fixes applied to the narration text before it hits
 *  ElevenLabs. Editing a rule re-narrates the affected stories on next listen. */
const PronunciationCard = ({ token }) => {
  const [rules, setRules] = useState([]);
  const [draft, setDraft] = useState(emptyRule());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [testText, setTestText] = useState('');
  const [testResult, setTestResult] = useState(null);

  const authHeaders = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/pronunciations`, { headers: authHeaders });
      const data = await res.json();
      setRules(data.items || []);
    } catch (e) {
      setError(e.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!draft.term.trim() || !draft.say_as.trim()) return;
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/pronunciations`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify(draft),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not add that rule.');
      setDraft(emptyRule());
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const patch = async (id, update) => {
    setError('');
    try {
      const res = await fetch(`${API}/api/admin/pronunciations/${id}`, {
        method: 'PUT', headers: authHeaders, body: JSON.stringify(update),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Update failed.');
      }
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this pronunciation rule?')) return;
    await fetch(`${API}/api/admin/pronunciations/${id}`, { method: 'DELETE', headers: authHeaders });
    await load();
  };

  const runTest = async () => {
    if (!testText.trim()) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/admin/pronunciations/test`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify({ text: testText }),
      });
      setTestResult(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-4 space-y-4" data-testid="pronunciation-card">
      <div>
        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-red-400 flex items-center gap-2">
          <SpellCheck2 className="h-3.5 w-3.5" /> Pronunciation list
        </p>
        <p className="text-xs text-zinc-500 leading-snug mt-1 max-w-3xl">
          Spell a name or number the way it should sound and the narrator will say it that way.
          Example: <span className="text-zinc-300">1994 → nineteen ninety four</span>,
          {' '}<span className="text-zinc-300">$2.5m → two point five million dollars</span>,
          {' '}<span className="text-zinc-300">Naumenko → Now-men-koh</span>.
          Stories containing a changed word are re-narrated the next time someone listens.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded border border-red-500/50 bg-red-900/20 px-3 py-2 text-xs font-bold text-red-300" data-testid="pronunciation-error">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      <form onSubmit={add} className="grid gap-2 sm:grid-cols-[1fr_1fr_auto] items-end" data-testid="pronunciation-form">
        <div>
          <label className="text-[10px] font-black uppercase tracking-[0.25em] text-zinc-400">Written as</label>
          <input
            type="text"
            value={draft.term}
            maxLength={120}
            onChange={(e) => setDraft({ ...draft, term: e.target.value })}
            placeholder="Naumenko"
            data-testid="pronunciation-term"
            className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
          />
        </div>
        <div>
          <label className="text-[10px] font-black uppercase tracking-[0.25em] text-zinc-400">Say it as</label>
          <input
            type="text"
            value={draft.say_as}
            maxLength={200}
            onChange={(e) => setDraft({ ...draft, say_as: e.target.value })}
            placeholder="Now-men-koh"
            data-testid="pronunciation-say-as"
            className="w-full mt-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
          />
        </div>
        <button
          type="submit"
          disabled={busy}
          data-testid="pronunciation-add-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-[11px] font-black uppercase tracking-widest"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          Add
        </button>
      </form>

      {rules.length === 0 ? (
        <p className="text-xs text-zinc-600" data-testid="pronunciation-empty">
          No rules yet. Everything is read exactly as written.
        </p>
      ) : (
        <div className="space-y-2" data-testid="pronunciation-list">
          {rules.map((r) => (
            <div
              key={r.id}
              data-testid={`pronunciation-row-${r.id}`}
              className="flex flex-wrap items-center gap-2 rounded border border-zinc-800 bg-black/40 px-3 py-2"
            >
              <code className="text-xs text-zinc-300">{r.term}</code>
              <span className="text-zinc-600">→</span>
              <code className="text-xs text-emerald-300 flex-1 min-w-[8rem]">{r.say_as}</code>
              <label className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={r.enabled !== false}
                  onChange={(e) => patch(r.id, { enabled: e.target.checked })}
                  data-testid={`pronunciation-toggle-${r.id}`}
                  className="h-3.5 w-3.5 accent-red-600"
                />
                On
              </label>
              <button
                type="button"
                onClick={() => remove(r.id)}
                data-testid={`pronunciation-delete-${r.id}`}
                className="p-1.5 rounded bg-zinc-900 hover:bg-red-900/40 border border-zinc-700 text-zinc-400 hover:text-red-300"
                title="Delete rule"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="border-t border-zinc-800 pt-3">
        <label className="text-[10px] font-black uppercase tracking-[0.25em] text-zinc-400">
          Try a sentence
        </label>
        <div className="flex gap-2 mt-1">
          <input
            type="text"
            value={testText}
            maxLength={2000}
            onChange={(e) => setTestText(e.target.value)}
            placeholder="Paste a line from a story to see how it will be read…"
            data-testid="pronunciation-test-input"
            className="flex-1 bg-zinc-950 border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-sm text-white outline-none"
          />
          <button
            type="button"
            onClick={runTest}
            disabled={busy || !testText.trim()}
            data-testid="pronunciation-test-btn"
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 border border-zinc-700 text-white text-[10px] font-black uppercase tracking-widest"
          >
            <Wand2 className="h-3 w-3" /> Check
          </button>
        </div>
        {testResult && (
          <p className="text-xs mt-2 leading-snug" data-testid="pronunciation-test-result">
            <span className="text-zinc-500">Narrator will say: </span>
            <span className={testResult.changed ? 'text-emerald-300' : 'text-zinc-400'}>
              {testResult.rewritten}
            </span>
            {!testResult.changed && <span className="text-zinc-600"> (no rules matched)</span>}
          </p>
        )}
      </div>
    </div>
  );
};

export default PronunciationCard;
